import { create } from "zustand";

const STORAGE_KEY = "talent-candidate-selection";

type SelectionsByCampaign = Record<string, string[]>;

interface CandidateSelectionState {
  selectionsByCampaign: SelectionsByCampaign;
  toggle: (campaignId: string, id: string) => void;
  setMany: (campaignId: string, ids: string[], selected: boolean) => void;
  clear: (campaignId: string) => void;
}

function readPersisted(): SelectionsByCampaign {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const result: SelectionsByCampaign = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
        result[key] = value as string[];
      }
    }
    return result;
  } catch {
    return {};
  }
}

function persist(selectionsByCampaign: SelectionsByCampaign) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selectionsByCampaign));
  } catch {
    // ignore — quota, private mode, etc.
  }
}

function pruneEmpty(selections: SelectionsByCampaign): SelectionsByCampaign {
  const next: SelectionsByCampaign = {};
  for (const [key, value] of Object.entries(selections)) {
    if (value.length > 0) next[key] = value;
  }
  return next;
}

export const useCandidateSelectionStore = create<CandidateSelectionState>((set) => ({
  selectionsByCampaign: readPersisted(),

  toggle: (campaignId, id) =>
    set((state) => {
      const current = state.selectionsByCampaign[campaignId] ?? [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      const merged = pruneEmpty({ ...state.selectionsByCampaign, [campaignId]: next });
      persist(merged);
      return { selectionsByCampaign: merged };
    }),

  setMany: (campaignId, ids, selected) =>
    set((state) => {
      const current = new Set(state.selectionsByCampaign[campaignId] ?? []);
      if (selected) {
        for (const id of ids) current.add(id);
      } else {
        for (const id of ids) current.delete(id);
      }
      const merged = pruneEmpty({
        ...state.selectionsByCampaign,
        [campaignId]: [...current],
      });
      persist(merged);
      return { selectionsByCampaign: merged };
    }),

  clear: (campaignId) =>
    set((state) => {
      if (!state.selectionsByCampaign[campaignId]) return state;
      const next = { ...state.selectionsByCampaign };
      delete next[campaignId];
      persist(next);
      return { selectionsByCampaign: next };
    }),
}));

const EMPTY_SELECTION: string[] = [];

export function useCampaignSelection(campaignId: string): string[] {
  return useCandidateSelectionStore(
    (state) => state.selectionsByCampaign[campaignId] ?? EMPTY_SELECTION,
  );
}
